import { DestroyRef, Directive, inject, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Directive({
  selector: '[idtResizeListener]',
  host: { '(window:resize)': 'onWindowResize()' },
})
/**
 * Directive that emits a debounced event whenever the browser window is resized.
 *
 * Attaches a window:resize listener on the host and debounces resize events by 500ms
 * to avoid excessive change detection. Consumers can bind to the `resizeEvent` output
 * to react to size changes (e.g., trigger layout recalculations).
 *
 * @example
 * <!-- In a template -->
 * <div idtResizeListener (resizeEvent)="onResize()"></div>
 */
export class ResizeListenerDirective {
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Output event emitted after a debounced window resize.
   *
   * Consumers can bind to this event to react to layout changes.
   * @example
   * <div idtResizeListener (resizeEvent)="onResize()"></div>
   */
  readonly resizeEvent = output();

  private readonly resizeSubject$ = new Subject<void>();

  constructor() {
    this.resizeSubject$
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.resizeEvent.emit();
      });
  }

  /**
   * Internal handler invoked by the window:resize host listener.
   * Pushes a value into the debounced subject which will emit `resizeEvent` after 500ms.
   */
  onWindowResize() {
    this.resizeSubject$.next();
  }
}
